import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { AppMainView } from '../../../../components/AppView'
import AppHeader from '../../../../components/AppView/Header'
import { Currency } from '../../../../components/Currency'
import { CurrentUserStore } from '../../../../stores/CurrentUserStore'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { API, IPricingGroup, PermissionNames } from '@getgreenline/homi-shared'
import { EditPricingGroupModal } from './EditPricingGroupModal'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import Helmet from 'react-helmet'

interface State {
  pricingGroups?: IPricingGroup[]
  isModalOpen: boolean
  selectedPricingGroupId?: string
}

interface Props {
  currentUserStore: CurrentUserStore
  currentCompanyStore: CurrentCompanyStore
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class PricingGroups extends React.Component<Props, State> {
  company = this.props.currentCompanyStore!.company!

  constructor(props: Props) {
    super(props)
    this.state = {
      isModalOpen: false,
    }
  }

  componentDidMount() {
    this.reload()
  }

  reload() {
    const company = this.props.currentCompanyStore!.company!
    API.getPricingGroups(company.id).then((pricingGroups) => {
      this.setState({ pricingGroups: pricingGroups })
    })
  }

  deletePricingGroup(pricingGroup: IPricingGroup) {
    const company = this.props.currentCompanyStore!.company!
    if (
      window.confirm(
        'Are you sure? Your attached products will be detached, and their product data will stay as is',
      )
    ) {
      API.deletePricingGroup(company.id, pricingGroup.id).then(() => {
        this.reload()
      })
    }
  }

  render() {
    const pricingGroups = this.state.pricingGroups || []

    // TODO: Check for edit product permissions
    const canEditProducts = this.props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_PRODUCTS,
      this.company.id,
    )

    return (
      <div>
        <Helmet>
          <title>Products | Pricing Groups</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <AppHeader title='Pricing groups'>
          <BlazeButton
            buttonProps={{
              type: 'primary',
              icon: 'plus',
              onClick: () =>
                this.setState({ isModalOpen: true, selectedPricingGroupId: undefined }),
              disabled: !canEditProducts,
            }}
            titleProps={{
              title: 'Add a pricing group',
            }}
          />
        </AppHeader>
        <AppMainView>
          <div className='container'>
            <div className='p-4 border border-secondary'>
              <b>Do you need to update prices for several products at once?</b>
              <div>
                Pricing groups help you manage pricing across many products by assigning them to the
                same pricing group.
              </div>
              <div>
                For example, you can get up $10/g, $12/g, $15/g pricing groups for easy pricing
                assignments.
              </div>
              <div>
                After creating a pricing group, edit your products to use prices from it. When you
                update a pricing group, attached products will automatically update their prices.
              </div>
            </div>
            <br />
            <table className='table'>
              <thead className='thead thead-light'>
                <tr>
                  <th>Name</th>
                  <th>Details</th>
                  <th>Options</th>
                </tr>
              </thead>
              <tbody>
                {pricingGroups.map((pricingGroup) => {
                  return (
                    <tr key={pricingGroup.id}>
                      {canEditProducts ? (
                        <>
                          <td>
                            <a
                              onClick={() => {
                                this.setState({
                                  isModalOpen: true,
                                  selectedPricingGroupId: pricingGroup.id,
                                })
                              }}
                            >
                              {pricingGroup.name}
                            </a>
                          </td>
                          <td>
                            {pricingGroup.childProducts.length > 0 ? (
                              <span>{pricingGroup.childProducts.length} variants</span>
                            ) : (
                              <Currency centValue={pricingGroup.price} />
                            )}
                          </td>
                          <td>
                            <a
                              className='text-danger'
                              onClick={() => {
                                this.deletePricingGroup(pricingGroup)
                              }}
                            >
                              Remove
                            </a>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className='text-muted'>{pricingGroup.name}</td>
                          <td className='text-muted'>(no edit permissions)</td>
                        </>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </AppMainView>
        {this.state.isModalOpen && (
          <EditPricingGroupModal
            pricingGroupId={this.state.selectedPricingGroupId || null}
            onClose={() => {
              this.setState({ isModalOpen: false })
              this.reload()
            }}
          />
        )}
      </div>
    )
  }
}
